import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Loader2,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { KanbanAddOrigin, useKanbanApi } from "@/utils/kanban-api";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  getEtapaDeEntrada,
  getEtapasDeDestino,
  useEnviarFreelancersParaVaga,
  type FreelancerParaVaga,
  type EnvioParaVagaResultado,
} from "@/hooks/use-enviar-freelancers-para-vaga";
import { VAGA_STATUS_COLUMNS, getVagaStatusInfo } from "@/utils/vaga-status";
import type { VagaListagem } from "@/types/vaga";
import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Todos os status menos "reprovado": vaga reprovada não recebe candidato novo. */
const STATUS_QUE_RECEBEM_CANDIDATO = VAGA_STATUS_COLUMNS.map((s) => s.id).filter(
  (status) => status !== "reprovado",
);

const LIMITE_VAGAS = 30;

function vagaTitulo(vaga: VagaListagem): string {
  return [vaga.agencia, vaga.cliente].filter(Boolean).join(" · ") || vaga.id.slice(0, 8);
}

function vagaDetalhe(vaga: VagaListagem): string {
  return [vaga.areas, vaga.descricao_curta].filter(Boolean).join(" · ");
}

interface EnviarParaVagaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancers: FreelancerParaVaga[];
  /** Quando aberto de dentro de uma vaga, ela sai da lista de destinos. */
  currentVagaId?: string;
  onSuccess?: () => void;
}

/**
 * Escolhe uma vaga e vincula os freelancers a ela (posição no kanban da vaga).
 * Aberto do menu de ações da listagem de freelancers, da barra de seleção em
 * massa e de dentro do quadro de uma vaga ("copiar para outra vaga").
 */
export function EnviarParaVagaDialog({
  open,
  onOpenChange,
  freelancers,
  currentVagaId,
  onSuccess,
}: EnviarParaVagaDialogProps) {
  const { toast } = useToast();
  const kanbanApi = useKanbanApi();
  const { enviar, isEnviando } = useEnviarFreelancersParaVaga();

  const [buscaVaga, setBuscaVaga] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [vagaSelecionada, setVagaSelecionada] = useState<VagaListagem | null>(null);
  const [etapaId, setEtapaId] = useState("");
  // Detalhamento do último envio: fica na tela em caso de falha parcial, pra o
  // usuário ver exatamente quem entrou, quem já estava e quem falhou.
  const [resultado, setResultado] = useState<EnvioParaVagaResultado | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(buscaVaga), 300);
    return () => clearTimeout(timer);
  }, [buscaVaga]);

  // Busca server-side (M164): não dá pra baixar as ~600 vagas só pra filtrar no cliente.
  const { data: vagas = [], isFetching: buscandoVagas } = useQuery({
    queryKey: ["vagas-para-envio", buscaDebounced],
    enabled: open,
    queryFn: async (): Promise<VagaListagem[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_vagas_list_page", {
        p_search: buscaDebounced.trim() || null,
        p_agencia_id: null,
        p_statuses: STATUS_QUE_RECEBEM_CANDIDATO,
        p_freelancer_id: null,
        p_date_field: null,
        p_date_from: null,
        p_date_to: null,
        p_sort: "created_desc",
        p_limit: LIMITE_VAGAS,
        p_offset: 0,
      });
      if (error) throw error;
      return (data ?? []) as VagaListagem[];
    },
  });

  const opcoesVaga = useMemo(
    () => vagas.filter((vaga) => vaga.id !== currentVagaId),
    [vagas, currentVagaId],
  );

  const { data: etapas = [], isFetching: carregandoEtapas } = useQuery({
    queryKey: ["kanban-etapas", vagaSelecionada?.id],
    enabled: !!vagaSelecionada,
    queryFn: () => kanbanApi.getEtapas(vagaSelecionada!.id),
  });

  const etapasDisponiveis = useMemo(() => getEtapasDeDestino(etapas), [etapas]);

  // Pré-seleciona a etapa de entrada ("Selecionados" nos templates padrão).
  useEffect(() => {
    if (!etapas.length) {
      setEtapaId("");
      return;
    }
    setEtapaId(getEtapaDeEntrada(etapas)?.id ?? "");
  }, [etapas]);

  const { data: cardsDaVaga = [] } = useQuery({
    queryKey: ["kanban-cards-all", vagaSelecionada?.id],
    enabled: !!vagaSelecionada,
    queryFn: () => kanbanApi.getCards(vagaSelecionada!.id, true),
  });

  const jaNaVaga = useMemo(() => {
    const idsNaVaga = new Set(cardsDaVaga.map((card) => card.freelancer_id));
    return freelancers.filter((freelancer) => idsNaVaga.has(freelancer.id));
  }, [cardsDaVaga, freelancers]);

  const todosJaNaVaga = !!vagaSelecionada && jaNaVaga.length === freelancers.length;
  const semEtapas = !!vagaSelecionada && !carregandoEtapas && etapasDisponiveis.length === 0;

  const limpar = () => {
    setBuscaVaga("");
    setBuscaDebounced("");
    setVagaSelecionada(null);
    setEtapaId("");
    setResultado(null);
  };

  const handleOpenChange = (novoOpen: boolean) => {
    if (!novoOpen) limpar();
    onOpenChange(novoOpen);
  };

  const handleEnviar = async () => {
    if (!vagaSelecionada || !etapaId) return;
    setResultado(null);

    const envio = await enviar(vagaSelecionada.id, etapaId, freelancers, currentVagaId ? KanbanAddOrigin.CopiarVaga : KanbanAddOrigin.Lista);
    const partes: string[] = [];
    if (envio.enviados.length > 0) partes.push(`${envio.enviados.length} vinculado(s)`);
    if (envio.jaExistentes.length > 0)
      partes.push(`${envio.jaExistentes.length} já estava(m) na vaga`);
    if (envio.falhas.length > 0) partes.push(`${envio.falhas.length} com erro`);

    toast({
      title:
        envio.enviados.length > 0
          ? `Enviado para ${vagaTitulo(vagaSelecionada)}`
          : "Nenhum freelancer vinculado",
      description: partes.join(" · "),
      variant: envio.falhas.length > 0 ? "destructive" : "default",
      action: (
        <ToastAction altText="Abrir a vaga" asChild>
          <Link to={`/vagas/${vagaSelecionada.id}`}>Ver vaga</Link>
        </ToastAction>
      ),
    });

    if (envio.enviados.length > 0) onSuccess?.();

    if (envio.falhas.length > 0) {
      setResultado(envio);
    } else {
      handleOpenChange(false);
    }
  };

  const umFreelancer = freelancers.length === 1;
  const statusInfo = getVagaStatusInfo(vagaSelecionada?.status);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>
            {currentVagaId ? "Enviar para outra vaga" : "Enviar para vaga"}
          </DialogTitle>
          <DialogDescription>
            {umFreelancer
              ? `Escolha a vaga que vai receber ${freelancers[0]?.nome}.`
              : `Escolha a vaga que vai receber os ${freelancers.length} freelancers selecionados.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {resultado && (
            <div className="space-y-2 rounded-md border p-3 text-sm">
              {resultado.enviados.length > 0 && (
                <div className="flex items-start gap-2 text-green-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>{resultado.enviados.length}</strong> vinculado(s):{" "}
                    {resultado.enviados.join(", ")}
                  </span>
                </div>
              )}
              {resultado.jaExistentes.length > 0 && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>{resultado.jaExistentes.length}</strong> já estava(m) na vaga:{" "}
                    {resultado.jaExistentes.join(", ")}
                  </span>
                </div>
              )}
              {resultado.falhas.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 font-medium text-red-700">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{resultado.falhas.length} com erro:</span>
                  </div>
                  <ul className="list-inside list-disc space-y-0.5 text-red-700">
                    {resultado.falhas.map((falha) => (
                      <li key={falha.nome}>
                        <span className="font-medium">{falha.nome}</span>: {falha.motivo}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    Pode tentar de novo: quem já entrou é ignorado.
                  </p>
                </div>
              )}
            </div>
          )}

          {!umFreelancer && (
            <div className="space-y-2">
              <Label>Freelancers</Label>
              <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                {freelancers.map((freelancer) => (
                  <Badge key={freelancer.id} variant="secondary" className="text-xs">
                    {freelancer.nome}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Vaga <span className="text-red-500">*</span>
            </Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="w-full justify-between gap-2 font-normal"
                  disabled={isEnviando}
                >
                  <span className="truncate text-left">
                    {vagaSelecionada ? vagaTitulo(vagaSelecionada) : "Buscar vaga..."}
                  </span>
                  <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[560px] p-0" align="start">
                {/* shouldFilter=false: quem filtra é o banco (RPC de busca). */}
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Buscar por agência, cliente, área..."
                    value={buscaVaga}
                    onValueChange={setBuscaVaga}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {buscandoVagas ? "Buscando vagas..." : "Nenhuma vaga encontrada."}
                    </CommandEmpty>
                    {opcoesVaga.map((vaga) => {
                      const info = getVagaStatusInfo(vaga.status);
                      const detalhe = vagaDetalhe(vaga);
                      return (
                        <CommandItem
                          key={vaga.id}
                          value={vaga.id}
                          onSelect={() => {
                            setVagaSelecionada(vaga);
                            setPickerOpen(false);
                          }}
                          className="items-start gap-2"
                        >
                          <Check
                            className={cn(
                              "mt-0.5 h-4 w-4 shrink-0",
                              vagaSelecionada?.id === vaga.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{vagaTitulo(vaga)}</div>
                            {detalhe && (
                              <div className="truncate text-xs text-muted-foreground">
                                {detalhe}
                              </div>
                            )}
                          </div>
                          {info && (
                            <Badge
                              variant="outline"
                              className={cn("shrink-0 text-xs", info.badgeClass)}
                            >
                              {info.title}
                            </Badge>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {vagaSelecionada && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {vagaDetalhe(vagaSelecionada) && <span>{vagaDetalhe(vagaSelecionada)}</span>}
                {statusInfo && (
                  <Badge variant="outline" className={cn("text-xs", statusInfo.badgeClass)}>
                    {statusInfo.title}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {vagaSelecionada && (
            <div className="space-y-2">
              <Label>
                Etapa <span className="text-red-500">*</span>
              </Label>
              <Select
                value={etapaId}
                onValueChange={setEtapaId}
                disabled={carregandoEtapas || semEtapas || isEnviando}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      carregandoEtapas ? "Carregando etapas..." : "Selecione uma etapa"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {etapasDisponiveis.map((etapa) => (
                    <SelectItem key={etapa.id} value={etapa.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: etapa.cor }}
                        />
                        {etapa.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {semEtapas && (
                <p className="text-xs text-destructive">
                  Esta vaga não tem etapas configuradas, então não é possível vincular
                  candidatos a ela.
                </p>
              )}
              {jaNaVaga.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {umFreelancer
                    ? "Este freelancer já é candidato desta vaga."
                    : `${jaNaVaga.length} de ${freelancers.length} já são candidatos desta vaga e serão ignorados.`}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isEnviando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={!vagaSelecionada || !etapaId || semEtapas || todosJaNaVaga || isEnviando}
          >
            {isEnviando ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : todosJaNaVaga ? (
              umFreelancer ? "Já está nesta vaga" : "Já estão nesta vaga"
            ) : (
              `Enviar${umFreelancer ? "" : ` ${freelancers.length - jaNaVaga.length}`}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
